import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import Button from '@/components/Button';
import { ArrowLeft, ExternalLink, Calendar, Tag, Globe, CheckCircle2, AlertCircle, Clock, RefreshCw } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { SolutionData, SolutionDetails, ExternalLinkData, OfficialWebsiteData, CapabilityDetail, UseCaseDetail, VersionDetail } from '@/types/solution';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useIsMobile } from '@/hooks/use-mobile';
import { retrievePreviewData, cleanupExpiredPreviews } from '@/utils/previewUtils';
import DynamicMetaTags from '@/components/DynamicMetaTags';
import MediaEmbed from '@/components/MediaEmbed';

// Type guards and utility functions (same as SolutionDetail)
const isDetailsObject = (details: any): details is SolutionDetails => {
  return details !== null && typeof details === 'object' && !Array.isArray(details);
};

const isCapabilityArray = (capabilities: any): capabilities is CapabilityDetail[] => {
  return Array.isArray(capabilities) && 
    capabilities.length > 0 && 
    typeof capabilities[0] === 'object' && 
    'title' in capabilities[0];
};

const isUseCaseArray = (useCases: any): useCases is UseCaseDetail[] => {
  return Array.isArray(useCases) && 
    useCases.length > 0 && 
    typeof useCases[0] === 'object' && 
    'title' in useCases[0];
};

const formatDate = (dateString: string | undefined): string => {
  if (!dateString) return '';
  
  const options: Intl.DateTimeFormatOptions = { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  };
  
  return new Date(dateString).toLocaleDateString('en-US', options);
};

const SolutionPreview = () => {
  const { tempId } = useParams<{ tempId: string }>();
  const isMobile = useIsMobile();
  const [solution, setSolution] = useState<SolutionData | null>(null);
  const [isError, setIsError] = useState(false);
  const [expandedCapability, setExpandedCapability] = useState<string | null>(null);
  const [expandedUseCase, setExpandedUseCase] = useState<string | null>(null);
  const [isVersionsOpen, setIsVersionsOpen] = useState(false);

  useEffect(() => {
    // Scroll to top when the component mounts or tempId changes
    window.scrollTo(0, 0);
    
    cleanupExpiredPreviews();
    
    if (tempId) {
      const data = retrievePreviewData(tempId);
      
      if (data) {
        setSolution(data);
      } else {
        setIsError(true);
      }
    } else {
      setIsError(true);
    }
  }, [tempId]);

  const latestVersion = useMemo(() => {
    if (!solution?.details?.versions || (solution.details.versions as VersionDetail[]).length === 0) {
      return null;
    }
    
    const versions = solution.details.versions as VersionDetail[];
    return versions.find(v => v.tags && Array.isArray(v.tags) && v.tags.includes('latest')) || versions[0];
  }, [solution]);

  const toggleCapability = (title: string) => {
    setExpandedCapability(expandedCapability === title ? null : title);
  };
  
  const toggleUseCase = (title: string) => {
    setExpandedUseCase(expandedUseCase === title ? null : title);
  };

  const handleRefresh = () => {
    if (tempId) {
      const data = retrievePreviewData(tempId);
      if (data) {
        setSolution(data);
        setIsError(false);
      }
    }
  };

  if (isError || !solution) {
    return (
      <Layout className="pt-16">
        <div className="container mx-auto px-4 py-16 text-center">
          <h1 className="text-2xl font-medium mb-4">Preview not found</h1>
          <p className="text-muted-foreground mb-6">
            The preview you're looking for doesn't exist or has expired.
          </p>
          <div className="flex gap-4 justify-center">
            <Button onClick={handleRefresh}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Link to="/">
              <Button variant="outline">
                Return Home
              </Button>
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  // Preview banner component
  const PreviewBanner = () => (
    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
          <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
            Preview Mode
          </span>
          <span className="text-xs text-blue-600 dark:text-blue-400">
            Generated at {new Date().toLocaleTimeString()}
          </span>
        </div>
        <Button variant="outline" size="sm" onClick={handleRefresh}>
          <RefreshCw className="w-3 h-3 mr-1" />
          Refresh
        </Button>
      </div>
    </div>
  );

  // Render sidebar content (same as SolutionDetail but simplified)
  const renderSidebarContent = () => (
    <div className="space-y-6">
      {solution.image && (
        <div className="rounded-xl border border-border bg-card overflow-hidden mb-6">
          <div className="aspect-video w-full overflow-hidden">
            <img 
              src={solution.image} 
              alt={solution.name}
              className="w-full h-full object-contain ai-solution-image"
            />
          </div>
        </div>
      )}
      
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <div className="p-5">
          {solution.officialWebsite ? (
            <a 
              href={solution.officialWebsite.url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center justify-between gap-3 group text-primary hover:text-primary/80 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Globe className="h-5 w-5" />
                <span className="font-medium">{solution.officialWebsite.title}</span>
              </div>
              <ExternalLink className="h-4 w-4 transition-colors" />
            </a>
          ) : (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Globe className="h-5 w-5" />
              <span className="font-medium">No website</span>
            </div>
          )}
        </div>
      </div>

      {solution.externalLinks && solution.externalLinks.length > 0 && (
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <div className="p-5 border-b border-border">
            <h3 className="font-medium">External Resources</h3>
          </div>
          <div className="p-5">
            <ul className="space-y-3">
              {solution.externalLinks.map((link, index) => (
                <li key={index}>
                  <a 
                    href={link.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center justify-between group text-muted-foreground hover:text-primary transition-colors"
                  >
                    <span>{link.title}</span>
                    <ExternalLink className="h-4 w-4 group-hover:text-primary transition-colors" />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );

  // Render header content (same as SolutionDetail)
  const renderHeaderContent = () => (
    <div>
      <div className="mb-3">
        <span className="text-sm font-medium px-3 py-1 bg-secondary rounded-full text-secondary-foreground">
          {solution.category}
        </span>
      </div>
      
      <h1 className="text-3xl md:text-4xl font-medium tracking-tight mb-4">
        {solution.name}
      </h1>
      
      <p className="text-muted-foreground text-lg mb-4 max-w-3xl">
        {solution.shortDescription}
      </p>
      
      <div className="flex flex-wrap gap-2 mb-4">
        {solution.tags.map((tag) => (
          <span 
            key={tag} 
            className="flex items-center gap-1 text-xs px-2.5 py-1 bg-primary/10 text-primary rounded-full"
          >
            <Tag className="h-3 w-3" />
            {tag}
          </span>
        ))}
      </div>
      
      <div className="flex items-center gap-6 text-sm text-muted-foreground mb-6">
        <div className="flex items-center gap-1.5">
          <span className="font-medium">Developer:</span> {solution.createdBy}
        </div>
        <div className="flex items-center gap-1.5">
          <Calendar className="h-3.5 w-3.5" />
          <span>Added: {solution.createdAt}</span>
        </div>
      </div>
    </div>
  );

  // Render main content (similar to SolutionDetail but simplified for preview)
  const renderMainContent = () => (
    <>
      {/* Versions Section */}
      {solution.details?.versions && solution.details.versions.length > 0 && (
        <div className="mb-10">
          <Collapsible open={isVersionsOpen} onOpenChange={setIsVersionsOpen}>
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-medium mb-4">Versions</h2>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm" className="p-0 h-9 w-9">
                  <ArrowLeft className={`h-4 w-4 transition-transform ${isVersionsOpen ? 'rotate-90' : '-rotate-90'}`} />
                </Button>
              </CollapsibleTrigger>
            </div>
            
            {!isVersionsOpen && latestVersion && (
              <div className="border rounded-lg overflow-hidden mb-2">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Version</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Release Date</TableHead>
                      <TableHead>Tags</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell className="font-medium">{latestVersion.version}</TableCell>
                      <TableCell>
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs ${
                          latestVersion.status === 'released' 
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' 
                            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                        }`}>
                          {latestVersion.status}
                        </span>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDate(latestVersion.since)}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {latestVersion.tags && latestVersion.tags.map((tag, tagIndex) => (
                            <span 
                              key={tagIndex} 
                              className={`text-xs px-2 py-0.5 rounded-full ${
                                tag === 'latest' 
                                  ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' 
                                  : tag === 'deprecated' 
                                    ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' 
                                    : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                              }`}
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            )}
            
            <CollapsibleContent>
              <div className="border rounded-lg overflow-hidden mb-2">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Version</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Release Date</TableHead>
                      <TableHead>Tags</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(solution.details?.versions as VersionDetail[]).map((version, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-medium">{version.version}</TableCell>
                        <TableCell>
                          <span className={`inline-flex px-2 py-1 rounded-full text-xs ${
                            version.status === 'released' 
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' 
                              : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                          }`}>
                            {version.status}
                          </span>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatDate(version.since)}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {version.tags && version.tags.map((tag, tagIndex) => (
                              <span 
                                key={tagIndex} 
                                className={`text-xs px-2 py-0.5 rounded-full ${
                                  tag === 'latest' 
                                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' 
                                    : tag === 'deprecated' 
                                      ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' 
                                      : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                                }`}
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>
      )}
      
      {/* Overview */}
      <div className="prose prose-gray max-w-none mb-10">
        <h2 className="text-2xl font-medium mb-4">Overview</h2>
        <div className="whitespace-pre-line text-muted-foreground leading-relaxed">
          {solution.details?.overview || solution.shortDescription}
        </div>
      </div>
      
      {/* Capabilities Section */}
      <div className="mb-10">
        <h2 className="text-2xl font-medium mb-4">Key Capabilities</h2>
        
        {solution.details?.capabilities && (
          <div className="space-y-4">
            {isCapabilityArray(solution.details.capabilities) ? (
              solution.details.capabilities.map((capability, index) => (
                <div key={index} className="border border-border/50 rounded-lg overflow-hidden">
                  <div 
                    className="flex items-start justify-between p-4 cursor-pointer hover:bg-muted/30 transition-colors"
                    onClick={() => toggleCapability(capability.title)}
                  >
                    <div className="flex items-start">
                      <CheckCircle2 className="h-5 w-5 text-primary/70 mr-3 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <h3 className="font-medium text-foreground">{capability.title}</h3>
                        {expandedCapability !== capability.title && (
                          <p className="text-muted-foreground text-sm line-clamp-1">{capability.description}</p>
                        )}
                      </div>
                    </div>
                    <ArrowLeft className={`h-4 w-4 mt-1 transition-transform ${expandedCapability === capability.title ? 'rotate-90' : '-rotate-90'}`} />
                  </div>
                  
                  {/* Single MediaEmbed instance (visible in both states) */}
                  {capability.mediaLink && (
                    <div className="px-4 pb-4">
                      <MediaEmbed 
                        url={capability.mediaLink} 
                        title=""
                        className={expandedCapability === capability.title ? "mb-3" : ""}
                      />
                    </div>
                  )}

                  {expandedCapability === capability.title && (
                    <div className="px-4 pb-4 pt-0 bg-muted/10">
                      <p className="text-muted-foreground mb-3">{capability.description}</p>
                      
                      {(capability.releaseDate || capability.lastUpdated) && (
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                          {capability.releaseDate && (
                            <div className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              <span>Released: {formatDate(capability.releaseDate)}</span>
                            </div>
                          )}
                          {capability.lastUpdated && (
                            <div className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              <span>Updated: {formatDate(capability.lastUpdated)}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <ul className="space-y-2 text-muted-foreground">
                {solution.details.capabilities.map((capability, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-primary/70 mr-3 mt-0.5 flex-shrink-0" />
                    {capability}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
      
      {/* Use Cases Section */}
      <div className="mb-10">
        <h2 className="text-2xl font-medium mb-4">Common Use Cases</h2>
        
        {solution.details?.useCases && (
          isUseCaseArray(solution.details.useCases) ? (
            <div className="space-y-4">
              {solution.details.useCases.map((useCase, index) => (
                <div key={index} className="border border-border/50 rounded-lg overflow-hidden bg-card">
                  <div 
                    className="flex items-start justify-between p-4 cursor-pointer hover:bg-muted/10 transition-colors"
                    onClick={() => toggleUseCase(useCase.title)}
                  >
                    <div className="flex-1">
                      <h3 className="font-medium text-foreground">{useCase.title}</h3>
                      {expandedUseCase !== useCase.title && (
                        <p className="text-muted-foreground text-sm line-clamp-1">{useCase.description}</p>
                      )}
                    </div>
                    <ArrowLeft className={`h-4 w-4 mt-1 transition-transform ${expandedUseCase === useCase.title ? 'rotate-90' : '-rotate-90'}`} />
                  </div>
                  
                  {/* Single MediaEmbed instance (visible in both states) */}
                  {useCase.mediaLink && (
                    <div className="px-4 pb-4">
                      <MediaEmbed 
                        url={useCase.mediaLink} 
                        title={expandedUseCase === useCase.title ? `${useCase.title} example` : ""}
                        className={expandedUseCase === useCase.title ? "mb-3" : ""}
                      />
                    </div>
                  )}

                  {expandedUseCase === useCase.title && (
                    <div className="px-4 pb-4 pt-0 bg-muted/5">
                      <p className="text-muted-foreground mb-3">{useCase.description}</p>
                      
                      {(useCase.releaseDate || useCase.lastUpdated) && (
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                          {useCase.releaseDate && (
                            <div className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              <span>Released: {formatDate(useCase.releaseDate)}</span>
                            </div>
                          )}
                          {useCase.lastUpdated && (
                            <div className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              <span>Updated: {formatDate(useCase.lastUpdated)}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {solution.details.useCases.map((useCase, index) => (
                <div key={index} className="p-4 rounded-lg border border-border/50 bg-card">
                  <p className="text-foreground">{useCase}</p>
                </div>
              ))}
            </div>
          )
        )}
      </div>
      
      {/* Limitations Section - Hidden */}
      {false && solution.details?.limitations && solution.details.limitations.length > 0 && (
        <div className="mb-6">
          <h2 className="text-2xl font-medium mb-4">Limitations</h2>
          <div className="p-4 rounded-lg border border-yellow-200/30 bg-yellow-50/30 dark:bg-yellow-900/10">
            <div className="flex gap-3">
              <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-yellow-700 dark:text-yellow-300 mb-2">Important considerations</h3>
                <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
                  {solution.details.limitations.map((limitation, index) => (
                    <li key={index}>{limitation}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );

  return (
          <Layout className="pt-16">
        <DynamicMetaTags
          title={`Preview: ${solution.name} - AIZen.tips`}
          description={`Preview of ${solution.name} solution. ${solution.shortDescription}`}
          canonical={`https://aizen.tips/solution/preview/${tempId}`}
          noIndex={true}
        />
      <div className="container mx-auto px-4 py-8">
        {/* Preview Banner */}
        <PreviewBanner />
        
        {/* Back navigation */}
        <div className="mb-6">
          <Button 
            variant="ghost" 
            icon={<ArrowLeft size={16} />} 
            iconPosition="left" 
            className="pl-0"
            onClick={() => {
              // Try to close the window if opened as popup, otherwise go back
              if (window.opener) {
                window.close();
              } else if (window.history.length > 1) {
                window.history.back();
              } else {
                window.close();
              }
            }}
          >
            Close Preview
          </Button>
        </div>
        
        {/* Layout based on screen size */}
        {isMobile ? (
          <>
            {renderHeaderContent()}
            <div className="mt-4">
              {renderMainContent()}
            </div>
            <div className="mt-8">
              {renderSidebarContent()}
            </div>
          </>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-8">
              {renderHeaderContent()}
              {renderMainContent()}
            </div>
            <div className="lg:col-span-4">
              <div className="sticky top-24">
                {renderSidebarContent()}
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default SolutionPreview;
